/**
 * RecordSheet — a record, opened from search, without opening its module.
 *
 * Tapping a search result used to navigate to the owning module's list page
 * filtered down to one row. On a phone that means a desktop table: most module
 * pages carry no mobile layout. This shows the record itself — what it is, its
 * state, where it is — and keeps the full page one tap away for the work that
 * genuinely needs it.
 *
 * Rendered through a portal to document.body on purpose. Its parent, the App
 * Finder panel, animates with `transform`, and a transformed ancestor becomes
 * the containing block for `position: fixed` — which would anchor this sheet to
 * the panel instead of the viewport and push it off-screen.
 */
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router';
import api from '../../utils/api';
import { T as DS } from '../../design-system/tokens';

export default function RecordSheet({ record, onClose, onNavigate }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!record) { setData(null); setError(''); return; }
    let live = true;
    setData(null);
    setError('');
    api.get(`/dashboard/record/${encodeURIComponent(record.category)}/${record.id}`)
      .then(({ data: d }) => { if (live) setData(d); })
      .catch(e => {
        if (!live) return;
        setError(e?.response?.data?.error || e.message || 'Could not load this record');
      });
    return () => { live = false; };
  }, [record]);

  // Escape closes, matching every other dismissable layer in the app.
  useEffect(() => {
    if (!record) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [record, onClose]);

  if (!record) return null;

  const sheet = (
    <div style={{ position: 'fixed', inset: 0, zIndex: 980, fontFamily: DS.font }}>
      <div
        onClick={onClose}
        style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.42)' }}
      />
      <div style={{
        position: 'absolute', left: 0, right: 0, bottom: 0,
        maxHeight: '78%', display: 'flex', flexDirection: 'column',
        background: DS.bg,
        borderRadius: `${DS.r.xl}px ${DS.r.xl}px 0 0`,
        boxShadow: '0 -4px 24px rgba(0,0,0,0.18)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 10, paddingBottom: 2, flexShrink: 0 }}>
          <div style={{ width: 32, height: 4, background: DS.borderStrong, borderRadius: 2 }} />
        </div>

        <div style={{
          padding: '10px 18px 12px', flexShrink: 0,
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12,
          borderBottom: `1px solid ${DS.divider}`,
        }}>
          <div style={{ minWidth: 0 }}>
            <div style={{
              fontSize: DS.type.xs, letterSpacing: '0.08em', textTransform: 'uppercase',
              color: DS.textMuted, fontWeight: 700,
            }}>
              {record.category}
            </div>
            <div style={{
              fontSize: DS.type.xl, fontFamily: DS.fontDisplay, color: DS.text,
              lineHeight: 1.2, marginTop: 3, wordBreak: 'break-word',
            }}>
              {data?.title || record.title}
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              width: 32, height: 32, borderRadius: DS.r.md, flexShrink: 0,
              background: DS.surfaceAlt, border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={DS.textSec} strokeWidth="2.2" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div style={{
          flex: 1, overflowY: 'auto', padding: '4px 18px 8px',
          WebkitOverflowScrolling: 'touch',
        }}>
          {error && (
            <div style={{ padding: '18px 0', color: DS.danger, fontSize: DS.type.sm }}>
              {error}
            </div>
          )}

          {!data && !error && (
            <div style={{ padding: '20px 0', color: DS.textMuted, fontSize: DS.type.sm }}>
              Loading…
            </div>
          )}

          {data?.fields?.map(f => (
            <div
              key={f.label}
              style={{
                display: 'flex', gap: 14, padding: '11px 0',
                borderBottom: `1px solid ${DS.divider}`,
                fontSize: DS.type.md,
              }}
            >
              <div style={{ width: 108, flexShrink: 0, color: DS.textMuted }}>{f.label}</div>
              <div style={{ flex: 1, color: DS.text, wordBreak: 'break-word' }}>{f.value}</div>
            </div>
          ))}

          {data && data.fields.length === 0 && (
            <div style={{ padding: '18px 0', color: DS.textMuted, fontSize: DS.type.sm }}>
              This record has no details beyond its name.
            </div>
          )}
        </div>

        <div style={{
          flexShrink: 0, padding: '12px 18px',
          paddingBottom: 'calc(16px + env(safe-area-inset-bottom, 0px))',
          borderTop: `1px solid ${DS.divider}`,
        }}>
          <button
            onClick={() => {
              const to = data?.link || record.link;
              onClose();
              onNavigate?.();   // also dismiss the search sheet behind this one
              navigate(to);
            }}
            style={{
              width: '100%', minHeight: 46, borderRadius: DS.r.pill, border: 'none',
              background: DS.accent, color: '#fff', cursor: 'pointer',
              fontSize: DS.type.md, fontWeight: 700, fontFamily: DS.font,
            }}
          >
            Open full page
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(sheet, document.body);
}
